import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Bike, Briefcase, User, Users, ArrowRight } from 'lucide-react';
import { useHomePersonas, HomePersona } from '../../hooks/useCommerce';

const ICONS: Record<string, React.ComponentType<{ size?: number; strokeWidth?: number }>> = {
  package: Package,
  bike: Bike,
  briefcase: Briefcase,
  user: User,
};

// Repli si la base est injoignable (= décision 2).
const FALLBACK: HomePersona[] = [
  { id: 'f1', icon: 'package', title: 'Livreur', subtitle: 'Livrez plus, dépensez moins. Chaque jour.', cta_label: "Découvrir l'offre", cta_url: '/products/sunu' },
  { id: 'f2', icon: 'bike', title: 'Taxi-moto', subtitle: 'Transportez plus de clients, générez plus de revenus.', cta_label: "Découvrir l'offre", cta_url: '/products' },
  { id: 'f3', icon: 'briefcase', title: 'Entreprise', subtitle: 'Électrifiez votre flotte, réduisez durablement vos coûts.', cta_label: "Découvrir l'offre", cta_url: '/products' },
  { id: 'f4', icon: 'user', title: 'Particulier', subtitle: 'Déplacez-vous librement, proprement et à moindre coût.', cta_label: "Découvrir l'offre", cta_url: '/products/teranga-plus' },
];

function CtaWrap({ to, className, children }: { to: string; className: string; children: React.ReactNode }) {
  if (/^https?:\/\//.test(to)) {
    return <a href={to} className={className}>{children}</a>;
  }
  return <Link to={to} className={className}>{children}</Link>;
}

function PersonaCard({ p, mobile }: { p: HomePersona; mobile?: boolean }) {
  const Icon = ICONS[p.icon] || User;
  return (
    <CtaWrap
      to={p.cta_url || '/products'}
      className={`group relative block overflow-hidden rounded-2xl border border-neutral-200 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-[0_18px_40px_-22px_rgba(10,22,40,0.35)] ${mobile ? 'shrink-0 w-[72%] snap-start' : ''}`}
    >
      <span className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary-600 to-emerald-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <span className="mb-3.5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
        <Icon size={22} strokeWidth={2} />
      </span>
      <h3 className="text-[17px] font-extrabold text-neutral-900">{p.title}</h3>
      {p.subtitle && <p className="mt-1.5 text-sm leading-snug text-neutral-500">{p.subtitle}</p>}
      <span className="mt-3.5 inline-flex items-center gap-1.5 text-sm font-bold text-primary-600">
        {p.cta_label || "Découvrir l'offre"}
        <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
      </span>
    </CtaWrap>
  );
}

const PersonaSegmentation: React.FC = () => {
  const { personas, ready } = useHomePersonas();
  const list = ready && personas && personas.length ? personas : FALLBACK;
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [nudge, setNudge] = useState(true);

  const onScroll = () => {
    const el = scroller.current;
    const card = el?.firstElementChild as HTMLElement | null;
    if (!el || !card) return;
    const step = card.offsetWidth + 12;
    setActive(Math.min(list.length - 1, Math.max(0, Math.round(el.scrollLeft / step))));
    if (nudge) setNudge(false);
  };

  return (
    <section className="bg-white py-12 md:py-16">
      <style>{`
        @keyframes persoNudge {0%,100%{transform:translateX(0)}40%{transform:translateX(-26px)}}
        .perso-nudge{animation:persoNudge 1.4s ease .6s 2}
        @media (prefers-reduced-motion: reduce){.perso-nudge{animation:none}}
        .perso-swipe::-webkit-scrollbar{display:none}
      `}</style>

      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-8 text-center md:mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-primary-600">
            <Users size={15} /> Vous êtes… ?
          </div>
          <h2 className="mt-2 text-3xl font-black tracking-tight text-neutral-900 md:text-4xl">
            La bonne solution, <span className="text-primary-600">pour chaque profil</span>
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-neutral-500">
            Mobilité électrique pensée pour votre usage. Choisissez le vôtre.
          </p>
        </div>

        {/* Desktop : grille 4 */}
        <div className="hidden gap-5 md:grid md:grid-cols-4">
          {list.map((p) => (
            <PersonaCard key={p.id} p={p} />
          ))}
        </div>

        {/* Mobile : swipe + indices */}
        <div className="md:hidden">
          <div className="relative">
            <div
              ref={scroller}
              onScroll={onScroll}
              className={`perso-swipe flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 ${nudge ? 'perso-nudge' : ''}`}
            >
              {list.map((p) => (
                <PersonaCard key={p.id} p={p} mobile />
              ))}
            </div>
            <div className="pointer-events-none absolute bottom-1 right-0 top-0 w-9 bg-gradient-to-l from-white to-transparent" />
          </div>

          <div className="mt-3 flex justify-center gap-2">
            {list.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? 'w-5 bg-primary-600' : 'w-1.5 bg-primary-100'}`}
              />
            ))}
          </div>
          <div className="mt-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-neutral-400">
            <ArrowRight size={13} className="text-primary-600" /> Glissez pour voir les {list.length} profils
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonaSegmentation;
